import { toRaw } from 'vue'

import { t } from '@/i18n/i18n'
import { useGalleryStore } from '@/pages/gallery/gallery.store'
import {
  DeeplinkElementType,
  type MenuChoice,
  type MenuElement,
  type NavBarContent,
} from '@/pages/menuEntries/MenuEntry.type'
import { NavbarContentService } from '@/pages/menuEntries/navbarContentService'
import { PAGE_MODE, type PageMode } from '@/shared/constants'
import { ErrorService } from '@/shared/service/errorService'
import { IdsService } from '@/shared/service/utils.service'
import { SnackbarService } from '@/shared/snackbar/snackbar.service'
import { create } from '@/shared/utils/zustandVue'
import { cloneDeep } from '@/utils'

/** Same element of the navbar: both carry the same non-empty id. */
function isSameElement(
  a: MenuElement | null | undefined,
  b: MenuElement | null | undefined,
): boolean {
  return Boolean(a?.id) && a?.id === b?.id
}

type CurrentElement =
  | {
      element: MenuElement | undefined
      wantedIndex: number | undefined
      originalElement: MenuElement | undefined
    }
  | undefined

class MenuEntriesState {
  mode: PageMode = PAGE_MODE.VIEW
  navBar: NavBarContent = {
    elements: [],
    type: 'BOTTOM_NAV',
  }
  menuChoices: MenuChoice[] = []
  currentElement: CurrentElement = undefined
  showEditElementModal = false
  showDeleteElementModal = false
  showVisibilityElementModal = false
  loading = 0
}

type MenuEntriesStore = MenuEntriesState & {
  load: () => void
  unload: () => void
  openEditElementModal: (element: MenuElement) => void
  openVisibilityElementModal: (element: MenuElement) => void
  hideVisibilityElementModal: () => void
  openAddElementModal: (index: number) => void
  openDeleteElementModal: (element: MenuElement) => void
  goToView: () => void
  updateNavBar: (navBar: Partial<NavBarContent>) => void
  updateElementPreview: (element: MenuElement) => void
  revertElementPreview: () => void
  getAllContent: () => Promise<void>
  getAllChoices: () => Promise<void>
  editElement: (element: MenuElement) => Promise<void>
  deleteElement: (element: MenuElement) => Promise<void>
  addElement: (payload: {
    element: MenuElement
    index: number
  }) => Promise<void>
  moveElement: (payload: {
    oldPosition: number
    newPosition: number
    element: MenuElement
  }) => Promise<void>
  saveNavBar: () => Promise<void>
  openHome: () => void
}

export const useMenuEntriesStore = create<MenuEntriesStore>((set, get) => ({
  ...new MenuEntriesState(),

  load: () => {
    set((s) => ({ loading: s.loading + 1 }))
  },

  unload: () => {
    set((s) => ({ loading: s.loading - 1 }))
  },

  openEditElementModal: (element) => {
    set({
      mode: PAGE_MODE.EDIT,
      currentElement: {
        element,
        wantedIndex: undefined,
        originalElement: cloneDeep(element),
      },
      showEditElementModal: true,
    })
  },

  openVisibilityElementModal: (element) => {
    set({
      mode: PAGE_MODE.TOGGLING_VISIBILITY,
      currentElement: {
        element,
        wantedIndex: undefined,
        originalElement: cloneDeep(element),
      },
      showVisibilityElementModal: true,
    })
  },

  hideVisibilityElementModal: () => {
    set({ showVisibilityElementModal: false })
  },

  openAddElementModal: (index) => {
    set({
      mode: PAGE_MODE.ADD,
      currentElement: {
        element: undefined,
        wantedIndex: index,
        originalElement: undefined,
      },
      showEditElementModal: true,
    })
  },

  openDeleteElementModal: (element) => {
    set({
      mode: PAGE_MODE.DELETE,
      currentElement: {
        element,
        wantedIndex: undefined,
        originalElement: cloneDeep(element),
      },
      showDeleteElementModal: true,
    })
  },

  goToView: () => {
    set((s) => ({
      mode: PAGE_MODE.VIEW,
      loading: s.loading - 1,
      currentElement: undefined,
      showEditElementModal: false,
      showDeleteElementModal: false,
      showVisibilityElementModal: false,
    }))
  },

  updateNavBar: (navBar) => {
    set((s) => ({ navBar: { ...s.navBar, ...navBar } }))
  },

  updateElementPreview: (element) => {
    const state = get()
    const index = state.navBar.elements.findIndex(
      (e: MenuElement | undefined) => isSameElement(e, element),
    )
    if (index === -1) return
    const elements = [...state.navBar.elements]
    elements[index] = cloneDeep(element)
    // Mutate currentElement.element IN PLACE (matching the dev Vuex mutation
    // `state.currentElement.element = cloneDeep(element)`). Replacing the
    // whole currentElement object with a new reference triggers
    // `watch(currentElement)` in EditMenuElement, which re-inits
    // menuElementEdited, which re-fires the deep watcher, which calls this
    // again — an infinite loop that freezes the tab. Mutating the property
    // keeps the currentElement reference stable so that watcher stays quiet.
    if (state.currentElement) {
      state.currentElement.element = cloneDeep(element)
    }
    set({ navBar: { ...state.navBar, elements } })
  },

  revertElementPreview: () => {
    set((s) => {
      if (s.currentElement?.originalElement && s.currentElement.element) {
        const index = s.navBar.elements.findIndex(
          (e: MenuElement | undefined) =>
            isSameElement(e, s.currentElement?.originalElement),
        )
        if (index !== -1) {
          const elements = [...s.navBar.elements]
          elements[index] = cloneDeep(s.currentElement.originalElement)
          return { navBar: { ...s.navBar, elements } }
        }
      }
      return {}
    })
  },

  async getAllContent() {
    get().load()
    try {
      const { gallery } = useGalleryStore.getState()
      const navBarContent = await NavbarContentService.getNavbarContent(gallery)
      set({
        navBar: {
          fontSize: 14,
          ...navBarContent,
        },
      })
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },

  async getAllChoices() {
    get().load()
    try {
      const { gallery } = useGalleryStore.getState()
      const choices = await NavbarContentService.getChoices(gallery)
      set({ menuChoices: choices })
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },

  async editElement(element) {
    get().load()
    try {
      const { navBar } = get()
      const index = navBar.elements.findIndex(
        (listElement: MenuElement | undefined) =>
          isSameElement(listElement, element),
      )
      if (index === -1) {
        console.error('Menu element not found')
      } else {
        const elements = [...navBar.elements]
        elements[index] = {
          ...navBar.elements[index],
          ...cloneDeep(element),
        }
        set({ navBar: { ...navBar, elements } })
      }
      await NavbarContentService.save(get().navBar)
      SnackbarService.info(t('GENERAL.SAVED'))
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },

  async deleteElement(element) {
    get().load()
    try {
      if (!element.removable) {
        return
      }
      const { navBar } = get()
      // element may be a reactive proxy from the zustandVue bridge
      // (currentElement read in a component): unwrap it so the reference
      // comparison against the raw navBar elements works.
      const rawElement = toRaw(element)
      const index = navBar.elements.findIndex(
        (listElement: MenuElement | undefined) =>
          listElement != null &&
          (toRaw(listElement) === rawElement ||
            (rawElement.id !== '' && listElement.id === rawElement.id)),
      )
      if (index > -1) {
        const elements = [...navBar.elements]
        elements.splice(index, 1)
        set({ navBar: { ...navBar, elements } })
      }
      await NavbarContentService.save(get().navBar)
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },

  async addElement({ element, index }) {
    get().load()
    try {
      const { navBar } = get()
      if (navBar.elements.length >= 5 || index >= 5) {
        SnackbarService.error(
          'Cannot add another element inside the menu, the array is already at max size or the set index is out of bounds',
        )
      } else {
        if (!element.id) {
          element.id = IdsService.generateUuid()
        }
        element.position = index
        const elements = [...navBar.elements]
        elements.splice(index, 0, element)
        set({ navBar: { ...navBar, elements } })
      }
      await NavbarContentService.save(get().navBar)
      // The server sorts and validates the elements: reload to show what it kept.
      await get().getAllContent()
      const saved = get().navBar.elements.find(
        (e: MenuElement | undefined) => e?.position === index,
      )
      get().openEditElementModal(saved ?? element)
      SnackbarService.info(t('GENERAL.SAVED'))
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },

  async moveElement({ oldPosition, newPosition, element }) {
    get().load()
    try {
      if (oldPosition === newPosition) {
        return
      }
      const { navBar } = get()
      const elements = navBar.elements.map((el) => ({ ...el }))
      if (newPosition > oldPosition) {
        elements.forEach((el: MenuElement | undefined) => {
          if (el && el.position <= newPosition && el.position >= oldPosition) {
            el.position--
          }
        })
      } else {
        elements.forEach((el: MenuElement | undefined) => {
          if (el && el.position >= newPosition && el.position <= oldPosition) {
            el.position++
          }
        })
      }
      const movedEl = elements.find(
        (e: MenuElement | undefined) => e != null && e.id === element.id,
      )
      if (movedEl) {
        movedEl.position = newPosition
      }
      elements.sort(
        (e1: MenuElement | undefined, e2: MenuElement | undefined) =>
          (e1 ? e1.position : 1000) - (e2 ? e2.position : 1000),
      )
      set({ navBar: { ...navBar, elements } })
      await NavbarContentService.save(get().navBar)
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },

  async saveNavBar() {
    get().load()
    try {
      await NavbarContentService.save(get().navBar)
      SnackbarService.info(t('GENERAL.SAVED'))
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },

  openHome() {
    const home = get().navBar.elements.find(
      (e) => e.type === DeeplinkElementType.HOME,
    )
    if (home) {
      get().openEditElementModal(home)
    }
  },
}))
